import React, {useEffect, useMemo, useState} from 'react';
import {createRoot} from 'react-dom/client';
const stylesheet = `
* { box-sizing: border-box; }
html, body, #root { width: 100%; height: 100%; margin: 0; background: #000; overflow: hidden; }
body { font-family: Arial, sans-serif; }
.mupi { position: relative; width: 100%; height: 100%; min-height: 100dvh; background: #000; }
.mupi__media { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; animation: mupi-fade .8s ease both; }
.mupi__footer { position: absolute; z-index: 2; left: 0; right: 0; bottom: 0; height: 4.3vh; display: flex; align-items: center; padding: 0 2.5vw; background: #fff; color: #8f8f8f; font: 400 clamp(10px, .42vw, 18px) Arial, sans-serif; }
.mupi__footer img { width: 8.2vw; max-height: 3.2vh; object-fit: contain; object-position: left center; }
.mupi--loading { display: grid; place-items: center; color: #fff; }
.mupi--loading p { max-width: 80%; text-align: center; font-size: clamp(16px, 2vw, 32px); }
@keyframes mupi-fade { from { opacity: 0; } to { opacity: 1; } }
`;
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = stylesheet;
  document.head.appendChild(style);
}

declare const __FINDIFY_WEB_CONFIG__: Record<string, string> | undefined;
const config = typeof __FINDIFY_WEB_CONFIG__ === 'undefined' ? {} : __FINDIFY_WEB_CONFIG__;
const API_URL = config.API_URL || 'https://api.findify.cloud';
const WORKSPACE_ID = config.WORKSPACE_ID || '204';
const SLIDE_MS = 20_000;
const REFRESH_MS = 60_000;

const localized = (value: any) => {
  if (typeof value === 'string') return value;
  return value?.['pt-PT'] ?? value?.['en-US'] ?? value?.pt ?? Object.values(value ?? {})[0] ?? '';
};

const mediaUrl = (value: any): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value.startsWith('http') ? value : `${API_URL}/uploads/${value.replace(/^\//, '')}`;
  if (typeof value === 'number') return `${API_URL}/uploads/${value}`;
  return mediaUrl(value.file_url ?? value.file_url_path ?? value.file_path ?? value.url ?? value.src ?? value.media_url ?? value.id);
};

const mediaFrom = (value: any): {url: string; type: 'image' | 'video'} | undefined => {
  const url = mediaUrl(value);
  if (!url && value && typeof value === 'object' && !Array.isArray(value)) {
    for (const localizedMedia of Object.values(value)) {
      const nested = mediaFrom(localizedMedia);
      if (nested) return nested;
    }
    return undefined;
  }
  if (!url) return undefined;
  const type = String(value?.file_type ?? value?.mime_type ?? value?.type ?? url).toLowerCase().includes('video') || /\.(mp4|webm|ogg)(\?|$)/i.test(url) ? 'video' : 'image';
  return {url, type};
};

const poiMedia = (poi: any) => {
  // The API may leave the selected language empty while the shared media
  // field, or another language, still contains the published video.
  const video = mediaFrom(poi?.file_video_media) ??
    mediaFrom(poi?.file_video_media_by_language) ??
    mediaFrom(poi?.file_video);
  if (video) return video;
  return mediaFrom(poi?.featured_image_media) ??
    mediaFrom(poi?.featured_image_media_by_language) ??
    mediaFrom(poi?.featured_image);
};

const secondsFromCategory = (category: any) => {
  const value = localized(category?.description).match(/\d+(?:[.,]\d+)?/)?.[0];
  const seconds = value ? Number(value.replace(',', '.')) : 30;
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 30;
};

async function loadSlides() {
  const requestOptions: RequestInit = {cache: 'no-store'};
  const workspaceResponse = await fetch(`${API_URL}/mobile/workspaces?offset=0&limit=100&workspace_id_eq=${WORKSPACE_ID}`, requestOptions);
  if (!workspaceResponse.ok) throw new Error(`Workspace indisponível (${workspaceResponse.status})`);
  const workspace = (await workspaceResponse.json())?.results?.[0];
  if (!workspace) throw new Error('Workspace não encontrado.');
  const domain = workspace.primary_domain?.domain_name;
  const homepage = workspace.app_settings?.homepage_lines?.find((line: any) => line?.category);
  const categoryId = homepage?.category;
  if (!domain || !categoryId) throw new Error('Configure uma categoria na página inicial do Findify.');
  const categoriesResponse = await fetch(`${API_URL}/front/${domain}/categories?offset=0&limit=100`, requestOptions);
  if (!categoriesResponse.ok) throw new Error(`Categorias indisponíveis (${categoriesResponse.status})`);
  const categories = (await categoriesResponse.json())?.results ?? [];
  const category = categories.find((item: any) => String(item.category_id) === String(categoryId));
  if (!category || category.category_type !== 'points_of_interest') throw new Error('A categoria configurada não é de pontos de interesse.');
  const response = await fetch(`${API_URL}/front/${domain}/points_of_interest?offset=0&limit=100&categories.category.category_id=${encodeURIComponent(categoryId)}`, requestOptions);
  if (!response.ok) throw new Error(`Conteúdos indisponíveis (${response.status})`);
  const payload = await response.json();
  const slides = (payload?.results ?? [])
    .slice()
    .sort((a: any, b: any) => {
      const dateA = Date.parse(a?.make_public_on ?? a?.public_date ?? '') || 0;
      const dateB = Date.parse(b?.make_public_on ?? b?.public_date ?? '') || 0;
      return dateB - dateA;
    })
    .map(poiMedia)
    .filter(Boolean) as {url: string; type: 'image' | 'video'}[];
  return {slides, intervalMs: secondsFromCategory(category) * 1000, title: localized(workspace.app_settings?.general?.title) || localized(workspace.name) || 'Museu D. Diogo de Sousa', logo: workspace.app_settings?.app_logo_url};
}

const Mupi = () => {
  const [state, setState] = useState<{slides: {url: string; type: 'image' | 'video'}[]; intervalMs: number; title: string; logo?: string} | null>(null);
  const [index, setIndex] = useState(0);
  const [error, setError] = useState('A carregar…');
  useEffect(() => {
    let active = true;
    let refreshing = false;
    const refresh = async (initial = false) => {
      if (!navigator.onLine || refreshing) return;
      refreshing = true;
      try {
        const nextState = await loadSlides();
        if (!active) return;
        setState((current) => {
          const currentSignature = current ? JSON.stringify(current) : '';
          const nextSignature = JSON.stringify(nextState);
          if (currentSignature === nextSignature) return current;
          setIndex(0);
          return nextState;
        });
        setError('');
      } catch (reason) {
        if (initial && active) setError(reason instanceof Error ? reason.message : 'Não foi possível carregar o slideshow.');
        // During a background refresh, keep displaying the last valid data.
      } finally {
        refreshing = false;
      }
    };
    void refresh(true);
    const timer = window.setInterval(() => void refresh(), REFRESH_MS);
    const handleOnline = () => void refresh();
    window.addEventListener('online', handleOnline);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('online', handleOnline);
    };
  }, []);
  useEffect(() => { if (!state?.slides.length) return; const timer = window.setInterval(() => setIndex((value) => (value + 1) % state.slides.length), state.intervalMs || SLIDE_MS); return () => window.clearInterval(timer); }, [state]);
  const slide = useMemo(() => state?.slides[index % state.slides.length], [state, index]);
  if (!state) return <main className="mupi mupi--loading"><p>{error}</p></main>;
  if (!slide) return <main className="mupi mupi--loading"><p>Não existem imagens ou vídeos publicados na categoria configurada.</p></main>;
  return <main className="mupi" aria-label={state.title}>
    {slide.type === 'video' ? <video key={slide.url} className="mupi__media" src={slide.url} autoPlay muted playsInline /> : <img key={slide.url} className="mupi__media" src={slide.url} alt="" />}
    {state.logo && <footer className="mupi__footer"><img src={state.logo} alt={state.title} /></footer>}
  </main>;
};

createRoot(document.getElementById('root')!).render(<Mupi />);
